/**
 * Minimal PCM WAV (RIFF) encoder/decoder. 16-bit little-endian, interleaved.
 */
export interface WavInfo {
  sampleRate: number;
  channels: number;
  bitsPerSample: number;
  frames: number;
  durationSec: number;
}

export function encodeWav(channels: readonly Float32Array[], sampleRate: number): ArrayBuffer {
  if (channels.length === 0) throw new Error('No audio channels to encode.');
  const frames = channels[0]!.length;
  if (channels.some((c) => c.length !== frames)) throw new Error('Channels have different lengths.');
  const numChannels = channels.length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataBytes = frames * blockAlign;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  ascii(36, 'data');
  view.setUint32(40, dataBytes, true);
  let offset = 44;
  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < numChannels; c++) {
      // Hard clamp is the last line of defence; the render path normalises peaks first.
      const sample = Math.max(-1, Math.min(1, channels[c]![i]!));
      view.setInt16(offset, sample < 0 ? Math.round(sample * 0x8000) : Math.round(sample * 0x7fff), true);
      offset += 2;
    }
  }
  return buffer;
}

/** Parses a canonical PCM WAV header (as produced by encodeWav). */
export function parseWav(buffer: ArrayBuffer): WavInfo & { samples: Int16Array } {
  const view = new DataView(buffer);
  const tag = (offset: number) => String.fromCharCode(...new Uint8Array(buffer, offset, 4));
  if (buffer.byteLength < 44 || tag(0) !== 'RIFF' || tag(8) !== 'WAVE' || tag(12) !== 'fmt ' || tag(36) !== 'data') {
    throw new Error('Not a canonical PCM WAV file.');
  }
  const channels = view.getUint16(22, true);
  const sampleRate = view.getUint32(24, true);
  const bitsPerSample = view.getUint16(34, true);
  const dataBytes = view.getUint32(40, true);
  const frames = dataBytes / (channels * (bitsPerSample / 8));
  return {
    sampleRate,
    channels,
    bitsPerSample,
    frames,
    durationSec: frames / sampleRate,
    samples: new Int16Array(buffer.slice(44, 44 + dataBytes)),
  };
}

export interface LevelStats {
  peak: number;
  rms: number;
  /** Samples at or beyond full scale. */
  clipped: number;
}

export function levelStats(channels: readonly Float32Array[], from = 0, to = channels[0]?.length ?? 0): LevelStats {
  let peak = 0;
  let sum = 0;
  let count = 0;
  let clipped = 0;
  for (const c of channels) {
    for (let i = from; i < to; i++) {
      const v = Math.abs(c[i]!);
      if (v > peak) peak = v;
      if (v >= 0.9999) clipped++;
      sum += v * v;
      count++;
    }
  }
  return { peak, rms: count ? Math.sqrt(sum / count) : 0, clipped };
}
